import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type CardholderInfo } from '@dni-types/data-models/ecommerce-payment'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Input } from '@pets-ui/Input/Input'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import { CARDHOLDER_NAME_INPUT_FILTER_REPLACE_REGEX, DIGITS_INPUT_FILTER_REPLACE_REGEX, EMAIL_INPUT_FILTER_REPLACE_REGEX } from '@utils'
import { type ChangeEvent } from 'react'
import { useState } from 'react'

/** Self-made CreditCardholderFields */
interface CreditCardholderFieldsProps {
  cardholderNameTitle?: string
  cardholderEmailTitle?: string
  cardholderPhoneTitle?: string
  cardholderNamePlaceholder?: string
  cardholderEmailPlaceholder?: string
  cardholderCountryCodePlaceholder?: string
  cardholderPhonePlaceholder?: string
  cardholderEmailOrPhoneRequired?: string
  cardholderInfo: CardholderInfo
  setCardholderInfo?: (cardholderInfo: CardholderInfo) => void
  errorMessage: {
    cardholderName?: string
    cardholderEmail?: string
    cardholderPhone?: string
  }
}

export const CreditCardholderFields = (props: CreditCardholderFieldsProps) => {
  const { cardholderNameTitle, cardholderEmailTitle, cardholderPhoneTitle, cardholderNamePlaceholder, cardholderEmailPlaceholder, errorMessage, cardholderInfo, setCardholderInfo, cardholderCountryCodePlaceholder, cardholderPhonePlaceholder } = props
  const [cardholder, setCardholder] = useState(cardholderInfo)
  const handleChangeNameEn = useCallbackEvent((e: ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(CARDHOLDER_NAME_INPUT_FILTER_REPLACE_REGEX, '').toUpperCase()
    const updated = {
      ...cardholder,
      name_en: filtered,
    }
    setCardholder(updated)
    setCardholderInfo?.(updated)
  })

  const handleChangeEmail = useCallbackEvent((e: ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(EMAIL_INPUT_FILTER_REPLACE_REGEX, '')
    const updated = {
      ...cardholder,
      email: filtered,
    }
    setCardholder(updated)
    setCardholderInfo?.(updated)
  })

  const handleChangeCountryCode = useCallbackEvent((e: ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(DIGITS_INPUT_FILTER_REPLACE_REGEX, '')
    const updated = {
      ...cardholder,
      phone_number_country_code: filtered,
    }
    setCardholder(updated)
    setCardholderInfo?.(updated)
  })

  const handleChangePhoneNumber = useCallbackEvent((e: ChangeEvent<HTMLInputElement>) => {
    const filtered = e.target.value.replace(DIGITS_INPUT_FILTER_REPLACE_REGEX, '')
    const updated = {
      ...cardholder,
      phone_number: filtered,
    }
    setCardholder(updated)
    setCardholderInfo?.(updated)
  })

  return (
    <VStack gap="4px">
      <VStack gap="4px">
        <Text color={colors.grey800}>{cardholderNameTitle}</Text>
        <Input
          fullWidth
          value={cardholder?.name_en}
          onChange={handleChangeNameEn}
          maxLength={45}
          placeholder={cardholderNamePlaceholder}
        />
        <Text color="red">{errorMessage?.cardholderName}</Text>
      </VStack>
      <VStack gap="4px">
        <Text color={colors.grey800}>{cardholderEmailTitle}</Text>
        <Input
          fullWidth
          value={cardholder?.email}
          onChange={handleChangeEmail}
          placeholder={cardholderEmailPlaceholder}
        />
        <Text color="red">{errorMessage?.cardholderEmail}</Text>
      </VStack>
      <VStack gap="4px" mb="8px">
        <Text color={colors.grey800}>{cardholderPhoneTitle}</Text>
        <VStack gap="4px">
          <HStack gap="8px" flexWrap="nowrap">
            <Box width="60px">
              {/** phone: country code */}
              <Input
                fullWidth
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cardholder?.phone_number_country_code}
                onChange={handleChangeCountryCode}
                maxLength={3}
                placeholder={cardholderCountryCodePlaceholder}
              />
            </Box>
            <Box flexGrow={1}>
              {/** phone: phone number */}
              <Input
                fullWidth
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={cardholder?.phone_number}
                onChange={handleChangePhoneNumber}
                placeholder={cardholderPhonePlaceholder}
              />
            </Box>
          </HStack>
          <Text color="red">{errorMessage?.cardholderPhone}</Text>
        </VStack>
      </VStack>
      <Box mb="8px">
        <Text variant="caption" color={colors.grey600}>
          {props.cardholderEmailOrPhoneRequired}
        </Text>
      </Box>
    </VStack>
  )
}
